import React, { useEffect, useState } from 'react';

import { useTranslation } from '../../i18n';
import './CookieConsentBanner.css';

const STORAGE_KEY = 'securepaper_cookie_consent';

const getInitialConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [consent, setConsent] = useState(() => getInitialConsent());
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: true,
    functional: true,
  });

  const isBannerVisible = consent === null;

  useEffect(() => {
    // Expose a simple API for opening preferences from anywhere (e.g., footer link)
    window.securepaperOpenCookiePreferences = () => {
      setShowPreferences(true);
    };
    return () => {
      if (window.securepaperOpenCookiePreferences) {
        delete window.securepaperOpenCookiePreferences;
      }
    };
  }, []);

  useEffect(() => {
    // If we already have consent, hydrate preferences from it
    if (consent && consent.preferences) {
      setPreferences(prev => ({ ...prev, ...consent.preferences }));
    }
  }, [consent]);

  const saveConsent = (type, prefsOverride) => {
    const payload = {
      type, // 'accept_all', 'reject_all', 'custom'
      timestamp: new Date().toISOString(),
      preferences:
        type === 'accept_all'
          ? { analytics: true, functional: true }
          : type === 'reject_all'
          ? { analytics: false, functional: true } // keep functional as essential
          : prefsOverride || preferences,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
    setConsent(payload);
    setShowPreferences(false);
    // Notify listeners (e.g., the analytics loader in App) that consent changed
    try {
      window.dispatchEvent(new Event('securepaper-consent-changed'));
    } catch {
      // ignore environments without Event support
    }
  };

  if (!isBannerVisible && !showPreferences) return null;

  return (
    <div className="cookie-banner">
      {isBannerVisible && (
      <div className="cookie-banner-inner">
        <div className="cookie-banner-main">
          <h3 className="cookie-banner-title">{t('cookie.title')}</h3>
          <p className="cookie-banner-text">
            {t('cookie.text')}
          </p>
          <div className="cookie-banner-links">
            <a href="/privacy">{t('cookie.privacyPolicy')}</a>
            <button
              type="button"
              className="cookie-link-button"
              onClick={() => {
                setShowPreferences(true);
              }}
            >
              {t('cookie.cookieSettings')}
            </button>
            <button
              type="button"
              className="cookie-link-button"
              onClick={e => {
                e.preventDefault();
                setShowPreferences(true);
              }}
            >
              {t('cookie.doNotShare')}
            </button>
          </div>
        </div>

        <div className="cookie-banner-actions">
          <button
            type="button"
            className="cookie-btn cookie-btn-secondary"
            onClick={() => saveConsent('reject_all')}
          >
            {t('cookie.rejectAll')}
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn-primary"
            onClick={() => saveConsent('accept_all')}
          >
            {t('cookie.acceptAll')}
          </button>
        </div>
      </div>
      )}

      {showPreferences && (
        <div className="cookie-preferences-overlay" role="dialog" aria-modal="true">
          <div className="cookie-preferences-modal">
            <div className="cookie-preferences-header">
              <h3>{t('cookie.preferencesTitle')}</h3>
              <button
                type="button"
                className="cookie-close-button"
                onClick={() => setShowPreferences(false)}
                aria-label={t('cookie.close')}
              >
                ×
              </button>
            </div>
            <p className="cookie-preferences-text">
              {t('cookie.preferencesText')}
            </p>

            <div className="cookie-preferences-list">
              <div className="cookie-preference-item">
                <div>
                  <h4>{t('cookie.essential')}</h4>
                  <p>{t('cookie.essentialDesc')}</p>
                </div>
                <span className="cookie-pill cookie-pill-required">{t('cookie.alwaysActive')}</span>
              </div>

              <div className="cookie-preference-item">
                <div>
                  <h4>{t('cookie.analytics')}</h4>
                  <p>{t('cookie.analyticsDesc')}</p>
                </div>
                <label className="cookie-switch">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={e =>
                      setPreferences(prev => ({ ...prev, analytics: e.target.checked }))
                    }
                  />
                  <span className="cookie-switch-slider" />
                </label>
              </div>

              <div className="cookie-preference-item">
                <div>
                  <h4>{t('cookie.functional')}</h4>
                  <p>{t('cookie.functionalDesc')}</p>
                </div>
                <label className="cookie-switch">
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={e =>
                      setPreferences(prev => ({ ...prev, functional: e.target.checked }))
                    }
                  />
                  <span className="cookie-switch-slider" />
                </label>
              </div>
            </div>

            <div className="cookie-preferences-actions">
              <button
                type="button"
                className="cookie-btn cookie-btn-secondary"
                onClick={() => saveConsent('reject_all')}
              >
                {t('cookie.rejectNonEssential')}
              </button>
              <button
                type="button"
                className="cookie-btn cookie-btn-primary"
                onClick={() => saveConsent('custom')}
              >
                {t('cookie.savePreferences')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsentBanner;


